/** biome-ignore-all lint/correctness/useExhaustiveDependencies: <explanation> */

import type { PageDef } from '@cobliteam/dashboard/src/dashboardDef';
import { useGetDevices } from '@cobliteam/hooks/src/useEntity/useDevices';
import useDriver from '@cobliteam/hooks/src/useEntity/useDriver';
import { useGetAllGeofences } from '@cobliteam/hooks/src/useEntity/useGeofenceV3';
import useVehicle from '@cobliteam/hooks/src/useEntity/useVehicle';
import useVehicleGroup from '@cobliteam/hooks/src/useEntity/useVehicleGroup';
import { useLanguage, useTranslation } from '@cobliteam/i18n';
import { usePermissions } from '@cobliteam/permissions';
import { getTextForLanguage } from '@cobliteam/ui-business/utils';
import diacritics from 'diacritics';
import Fuse from 'fuse.js';
import { useGlobalSidebarDef } from '../globalSidebarDef';
import { type DataWithCategory, EnumCategory } from '../types';

const useFetchData = ({ currentLocation, vehicleGroupIds, pagesFlatted }: { currentLocation: string; vehicleGroupIds: string[]; pagesFlatted: PageDef[] }) => {
  const { t } = useTranslation('global-search');
  const { currentLanguage } = useLanguage();
  const globalSidebarDef = useGlobalSidebarDef(t);
  const shouldIncludeObservationPeriod = currentLocation === '/monitoring' || currentLocation === '/devices';

  const { isAccessAllowed } = usePermissions();
  const isAllowedViewGeofences = isAccessAllowed('view:places-of-interest');

  // ## FIXME: fetch promiseAll
  const {
    data: vehicles,
    isPending: isPendingVehicles,
    isRefetching: isRefetchingVehicles,
    refetch: refetchVehicles,
    error: errorVehicles,
  } = useVehicle().getVehicles({}, vehicleGroupIds);
  const { data: drivers, isPending: isPendingDrivers, isRefetching: isRefetchingDrivers, refetch: refetchDrivers, error: errorDrivers } = useDriver().getDriversWithVehicles({});
  const {
    data: vehicleGroups,
    isPending: isPendingVehicleGroups,
    isRefetching: isRefetchingVehicleGroups,
    refetch: refetchVehicleGroups,
    error: errorVehicleGroups,
  } = useVehicleGroup().getVehicleGroups({});
  const {
    data: geofences,
    isPending: isPendingGeofences,
    isRefetching: isRefetchingGeofences,
    refetch: refetchGeofences,
    error: errorGeofences,
  } = useGetAllGeofences({ options: { enabled: isAllowedViewGeofences } });

  const {
    data: devices,
    isPending: isPendingDevices,
    isRefetching: isRefetchingDevices,
    refetch: refetchDevices,
    error: errorDevices,
  } = useGetDevices({ includeObservationPeriodTrigger: shouldIncludeObservationPeriod });

  const hasError = !!(errorVehicles || errorDrivers || errorVehicleGroups || errorDevices || errorGeofences);

  if (isPendingVehicles || isPendingDrivers || isPendingVehicleGroups || isPendingDevices || isPendingGeofences) {
    return {
      isLoading: true,
      error: hasError,
      vehicles: [],
      drivers: [],
      vehicleGroups: [],
      devices: [],
      geofences: [],
      fuseVehicles: undefined,
      fuseDrivers: undefined,
      fuseVehicleGroups: undefined,
      fuseDevices: undefined,
      fuseGeofences: undefined,
    };
  }

  const getCategory = (categoryId: EnumCategory) => {
    const group = globalSidebarDef.find((category) => category.categories.find((c) => c.id === categoryId));
    return group?.categories.find((c) => c.id === categoryId);
  };

  // ## transformers

  const vehiclesWithCategory =
    vehicles?.map((vehicle) => {
      return { ...vehicle, tagsIndex: vehicle.tags?.map((tag) => tag.name).join(', '), category: getCategory(EnumCategory.VEHICLES) };
    }) || [];

  const driversWithCategory =
    drivers?.map((driver) => {
      return { ...driver, category: getCategory(EnumCategory.DRIVERS) };
    }) || [];

  const groupsWithVehicles =
    vehicleGroups?.map((vehicleGroup) => {
      const newVehicles = vehicles?.filter((vehicle) => vehicleGroup.vehicleIds?.includes(vehicle.id));
      return { ...vehicleGroup, vehicles: newVehicles, category: getCategory(EnumCategory.VEHICLE_GROUPS) };
    }) || [];

  const devicesWithCategory =
    devices?.map((device) => {
      return { ...device, category: getCategory(EnumCategory.DEVICES) };
    }) || [];

  const geofencesWithCategory =
    geofences?.map((geofence) => {
      return { ...geofence, category: getCategory(EnumCategory.GEOFENCES) };
    }) || [];

  // const oramaIaWithCategory = [
  const pagesWithCategory =
    pagesFlatted
      .filter((page) => !page.sidebar?.hide)
      .filter((page) => page.url)
      .map((page) => {
        const pageTitle = getTextForLanguage(page.title, currentLanguage);
        const pageDescription = getTextForLanguage(page.description, currentLanguage);
        return {
          ...page,
          id: pageTitle?.toLowerCase().replace(/\s+/g, '-') || '',
          primary: page.primary || '',
          category: getCategory(EnumCategory.PAGES),
          description: pageDescription,
        };
      }) || [];

  const dataWithCategory = [
    ...vehiclesWithCategory,
    ...driversWithCategory,
    ...groupsWithVehicles,
    ...devicesWithCategory,
    ...geofencesWithCategory,
    ...pagesWithCategory,
  ] as DataWithCategory[];

  // ## fuse
  const fuseConfig = {
    getFn: (obj: any, path: string | string[]) => {
      const value = Fuse.config.getFn(obj, path);
      if (typeof value === 'string') {
        return diacritics.remove(value);
      }
      return value;
    },
  };

  const fuse = new Fuse(dataWithCategory || [], {
    ...fuseConfig,
    keys: [
      // pages
      'title',
      {
        name: 'primary',
        weight: 1,
      },
      'description',

      // vehicles
      'label',
      'id',
      'licensePlate',
      'cobliId',
      'tagsIndex',
      // drivers
      'name',
      'driverCode',
      'driverLicense',
      'registryId',
      'cpf',
      'identifier',
      // vehicleGroups
      'name',
      'id',
      'vehicles.label',
      'vehicles.id',
      'vehicles.licensePlate',
      'vehicles.cobliId',
      // devices
      'label',
      'id',
      'deviceId',
      'deviceCode',
      'deviceType',
      // geofences
      'name',
      'address',
      'id',
    ],
  });

  const handleUpdateData = () => {
    refetchVehicles();
    refetchDrivers();
    refetchVehicleGroups();
    refetchDevices();
    refetchGeofences();
    fuse.setCollection([]);
  };

  return {
    handleUpdateData,
    isRefetching: isRefetchingVehicles || isRefetchingDrivers || isRefetchingVehicleGroups || isRefetchingDevices || isRefetchingGeofences,
    isLoading: false,
    error: hasError,
    data: dataWithCategory || [],
    fuse,
  };
};

export default useFetchData;
